import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';

export default function DocumentationPage() {
  const gettingStartedSteps = [
    {
      label: 'Create Your Account',
      description:
        'Sign up at simplypayroll.com/register and choose your subscription plan.',
      icon: <CheckCircleIcon />,
    },
    {
      label: 'Complete Your Business Profile',
      description:
        'Add your business information including name, address, and FEIN. This information appears on 1099 forms.',
      icon: <CheckCircleIcon />,
    },
    {
      label: 'Add Your First Contractor',
      description:
        'Navigate to Payees → Add New Payee. Enter their legal name, address, Tax ID (SSN or EIN), and hourly rate.',
      icon: <PersonAddIcon />,
    },
    {
      label: 'Create a Timesheet',
      description:
        'Go to Timesheets, select your payee, choose the week, and enter hours worked. The system calculates gross pay automatically.',
      icon: <AccessTimeIcon />,
    },
    {
      label: 'Generate 1099 Forms',
      description:
        'At tax time, go to 1099 Records to generate forms for contractors who earned $600+. Download PDFs and file with the IRS.',
      icon: <DescriptionIcon />,
    },
  ];

  const features = [
    {
      title: 'Contractor Management',
      topics: [
        {
          name: 'Adding Payees',
          content:
            'Click "Add New Payee" and enter contractor information. Choose "Individual" for sole proprietors or "Business" for LLCs/corporations. Required fields: name, address, and Tax ID.',
        },
        {
          name: 'Hourly vs Flat Rate',
          content:
            'Hourly contractors are paid based on hours × rate. Flat rate contractors receive fixed payments. You can still track hours for flat rate contractors for project management purposes.',
        },
        {
          name: 'Activating/Deactivating',
          content:
            'Toggle contractor status to hide inactive contractors from dropdowns while preserving their historical data.',
        },
      ],
    },
    {
      title: 'Timesheet Tracking',
      topics: [
        {
          name: 'Creating Timesheets',
          content:
            'Select a payee and week ending date. Enter hours for each day. Add task descriptions and notes for detailed records. Hours are saved automatically as you type.',
        },
        {
          name: 'Week Ending Configuration',
          content:
            'Set your week ending day (default: Saturday) in Payment Settings. This determines how weeks are calculated for timesheet entry.',
        },
        {
          name: 'Editing Timesheets',
          content:
            'Timesheets can be edited anytime. Changes are immediately reflected in payment calculations and reports.',
        },
        {
          name: 'Keyboard Shortcuts',
          content:
            'Press Ctrl+S to save, Ctrl+N to add a new task row. Use Tab to navigate between fields.',
        },
      ],
    },
    {
      title: 'Payment Records',
      topics: [
        {
          name: 'Recording Payments',
          content:
            'Payments are created from submitted timesheets. SimplyPayroll calculates gross pay but does NOT process actual transfers. You must pay contractors separately.',
        },
        {
          name: 'Payment History',
          content:
            'View all payments by contractor, filter by date range, and export records. Use this for reconciliation and year-end reporting.',
        },
        {
          name: '$600 Threshold',
          content:
            'The IRS requires 1099 forms only for contractors earning $600+ per calendar year. SimplyPayroll automatically calculates this threshold.',
        },
      ],
    },
    {
      title: '1099 Tax Forms',
      topics: [
        {
          name: 'Generating 1099-MISC',
          content:
            'Go to 1099 Records, select the tax year, and click "Generate Forms". Only contractors who earned $600+ are included. Review each form before downloading.',
        },
        {
          name: 'Form Deadlines',
          content:
            'Deadline: January 31st. You must provide copies to contractors AND file with the IRS by this date. Late filing can result in penalties.',
        },
        {
          name: 'Filing with IRS',
          content:
            'SimplyPayroll generates PDF forms. You must file them yourself using the IRS FIRE system (recommended for electronic filing) or mail paper forms to the IRS.',
        },
        {
          name: 'Corrected Forms',
          content:
            'If you need to correct a filed 1099, regenerate it in SimplyPayroll, mark it as "CORRECTED", and refile with the IRS.',
        },
      ],
    },
    {
      title: 'Reports & Analytics',
      topics: [
        {
          name: 'Dashboard Overview',
          content:
            'View key metrics including total payments, active contractors, hours tracked, and contractors above the 1099 threshold.',
        },
        {
          name: 'Exporting Data',
          content:
            'Export timesheets, payments, and contractor information as CSV files from the Reports section. Use for accounting software or external analysis.',
        },
        {
          name: 'Audit Logs',
          content:
            'Admins can view activity logs showing who made changes and when. Useful for compliance and tracking team activity.',
        },
      ],
    },
    {
      title: 'Team Management',
      topics: [
        {
          name: 'Inviting Users',
          content:
            'Admins can invite team members via email. Invited users receive a link to set their password and access the account.',
        },
        {
          name: 'User Roles',
          content:
            'Admin: Full access to all features including billing and team management. User: Can manage payees and timesheets but cannot access billing or invite users.',
        },
        {
          name: 'Removing Access',
          content:
            'Admins can remove team members at any time. Removed users lose access immediately.',
        },
      ],
    },
  ];

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Documentation
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.secondary' }}>
            Everything you need to know about using SimplyPayroll
          </Typography>
        </Box>

        {/* Getting Started */}
        <Card elevation={2} sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              Getting Started
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Follow these steps to set up your account and start tracking
              contractor payroll.
            </Typography>

            <Stepper orientation="vertical">
              {gettingStartedSteps.map((step, index) => (
                <Step key={index} active>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            <Alert severity="success" sx={{ mt: 4 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Complete your business profile first
                before adding contractors. This ensures your 1099 forms have
                accurate payer information.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Feature Documentation */}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
          Feature Guides
        </Typography>

        {features.map((feature, idx) => (
          <Card key={idx} elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
              >
                {feature.title}
              </Typography>
              <Grid container spacing={3}>
                {feature.topics.map((topic, topicIdx) => (
                  <Grid item xs={12} key={topicIdx}>
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, fontSize: '1.1rem' }}
                        >
                          {topic.name}
                        </Typography>
                        <Chip
                          label="Guide"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {topic.content}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}

        {/* Best Practices */}
        <Card elevation={2} sx={{ mt: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              Best Practices
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}
                  >
                    ✓ Do
                  </Typography>
                  <Typography
                    component="ul"
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 2 }}
                  >
                    <li>
                      Collect W-9 forms from contractors before their first
                      payment
                    </li>
                    <li>Submit timesheets regularly (weekly recommended)</li>
                    <li>
                      Verify 1099 information before the January 31st deadline
                    </li>
                    <li>Keep records for at least 7 years (IRS requirement)</li>
                    <li>Review the $600 threshold report before year-end</li>
                    <li>
                      Back up your data regularly using the export feature
                    </li>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}
                  >
                    ✗ Don't
                  </Typography>
                  <Typography
                    component="ul"
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 2 }}
                  >
                    <li>
                      Don't rely on SimplyPayroll to process actual payments
                    </li>
                    <li>
                      Don't wait until tax time to verify contractor information
                    </li>
                    <li>Don't share your login credentials with contractors</li>
                    <li>
                      Don't forget to update addresses if contractors move
                    </li>
                    <li>Don't mix employee (W-2) and contractor (1099) data</li>
                    <li>
                      Don't assume SimplyPayroll handles tax withholding (it
                      doesn't)
                    </li>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Need More Help */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'info.50',
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'info.200',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Need more help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Check our Help Center for FAQs or contact support for personalized
            assistance.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <a href="/help" style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  display: 'inline-block',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                Visit Help Center
              </Typography>
            </a>
            <a href="/contact" style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  display: 'inline-block',
                  '&:hover': { bgcolor: 'primary.50' },
                }}
              >
                Contact Support
              </Typography>
            </a>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
